import { useState } from 'react';
import { Lock, CheckCircle, Download, PenTool, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { SignatureCanvas } from '../shared';

const MandatTab = ({ mandats, setMandats, user, activeSubscription }) => {
  const [signing, setSigning] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [formData, setFormData] = useState({ name: user?.name || '', address: '' });
  
  if (!activeSubscription?.can_sign_mandat) {
    return (
      <div className="text-center py-12">
        <Lock className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400">Souscrivez d'abord à un pack</p>
      </div>
    );
  }
  
  const handleSign = async () => {
    if (!signatureData || !formData.name || !formData.address) { 
      toast.error('Veuillez remplir tous les champs et signer'); 
      return; 
    }
    setSigning(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/mandats/sign`, { 
        client_name: formData.name, 
        client_address: formData.address, 
        signature_data: signatureData, 
        pack_type: activeSubscription.pack_type 
      }, { headers: { 'Authorization': `Bearer ${token}` }, withCredentials: true });
      toast.success('Mandat de Succès signé !');
      const mandatsRes = await axios.get(`${API}/mandats`, { headers: { 'Authorization': `Bearer ${token}` }, withCredentials: true });
      setMandats(mandatsRes.data.mandats);
    } catch (error) { 
      toast.error(error.response?.data?.detail || 'Erreur'); 
    } finally { 
      setSigning(false); 
    }
  };
  
  const handleDownloadPDF = async (mandatId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/mandats/${mandatId}/pdf`, { 
        headers: { 'Authorization': `Bearer ${token}` }, 
        responseType: 'blob', 
        withCredentials: true 
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); 
      link.href = url; 
      link.setAttribute('download', `Mandat_Succes_${mandatId}.pdf`); 
      document.body.appendChild(link); 
      link.click(); 
      link.remove();
    } catch { 
      toast.error('Erreur téléchargement'); 
    }
  };
  
  if (mandats && mandats.length > 0) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white mb-2">Mandat de Succès</h2>
          <p className="text-gray-400 font-mono text-sm">// DOCUMENT_SIGNÉ</p>
        </div>
        {mandats.map((m) => (
          <div key={m.mandat_id} className="glass-card p-4 md:p-6 border border-success">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <p className="text-success font-orbitron font-bold text-lg flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />Mandat Signé
                </p>
                <p className="text-gray-400 text-sm mt-1">Le {new Date(m.signed_at).toLocaleDateString('fr-FR')}</p>
              </div>
              <Button 
                onClick={() => handleDownloadPDF(m.mandat_id)} 
                className="bg-cyan-400 text-black hover:bg-cyan-300 w-full md:w-auto py-4" 
                data-testid="download-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />Télécharger PDF
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white mb-2">Signer le Mandat de Succès</h2>
        <p className="text-gray-400 font-mono text-sm">// ARTICLE_1984_CODE_CIVIL</p>
      </div>
      <div className="glass-card p-4 md:p-6 border border-gray-800 space-y-4">
        <div>
          <Label className="text-gray-400 text-sm">Nom complet</Label>
          <Input 
            value={formData.name} 
            onChange={(e) => setFormData({...formData, name: e.target.value})} 
            className="bg-gray-900 border-gray-700 text-white mt-1 py-4" 
            data-testid="mandat-name-input" 
          />
        </div>
        <div>
          <Label className="text-gray-400 text-sm">Adresse</Label>
          <Input 
            value={formData.address} 
            onChange={(e) => setFormData({...formData, address: e.target.value})} 
            placeholder="123 rue Example, 31000 Toulouse" 
            className="bg-gray-900 border-gray-700 text-white mt-1 py-4" 
            data-testid="mandat-address-input" 
          />
        </div>
        <div>
          <Label className="text-gray-400 text-sm mb-2 block">Signature manuscrite</Label>
          <SignatureCanvas onSignatureChange={setSignatureData} />
        </div>
        <Button 
          onClick={handleSign} 
          disabled={signing} 
          className="w-full bg-cyan-400 text-black hover:bg-cyan-300 py-6 text-lg" 
          data-testid="sign-mandat-btn"
        >
          {signing ? (
            <><Loader2 className="w-5 h-5 animate-spin mr-2" />Signature en cours...</>
          ) : (
            <><PenTool className="w-5 h-5 mr-2" />Signer le Mandat</>
          )}
        </Button>
      </div>
    </div>
  );
};

export { MandatTab };
